<script setup>
    import { computed, defineProps, defineEmits, ref, defineExpose, watchEffect } from 'vue';
    import utils from '@/commonProject/helpers/utils';

    // Store
    import { useLanguageStore } from '@/stores/common/languageStore';

    // ----------------------------
    // Props
    // ----------------------------
    const props = defineProps({
        // currentDate: {
        //     type: Date,
        //     required: true,
        // },
        reserveDateList: {
            type: Array,
            default: () => [],
        },
        reserveDate: {
            type: Date,
            default: new Date(),
        },
        resetFlag: {
            type: Boolean,
            default: false,
        },
    });

    // ----------------------------
    // Emits
    // ----------------------------
    const emit = defineEmits(['dateSelected', 'yearMonth']);
    // ----------------------------
    // 定数
    // ----------------------------
    const languageStore = useLanguageStore();

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    // 年月の初期設定
    const currentYear = ref(props.reserveDate.getFullYear());
    const currentMonth = ref(props.reserveDate.getMonth() + 1);
    // 現在の年月を基準として制限の設定
    const today = new Date();
    const minYear = today.getFullYear();
    const minMonth = today.getMonth() + 1;

    const selectedDate = ref(null);
    const weekDays = ['日', '月', '火', '水', '木', '金', '土'];
    const weekDaysEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    // ----------------------------
    // watch
    // ----------------------------
    watchEffect(
        () => props.reserveDate,
        (newDate) => {
            currentMonth.value = newDate.getMonth() + 1;
            currentYear.value = newDate.getFullYear();

            if (utils.isNullEmpty(selectedDate.value)) {
                selectedDate.value = today;
            }
            
        },
        { immediate: true }
    );

    // ----------------------------
    // 関数
    // ----------------------------
    /**
     * 指定された年と月のカレンダーデータを生成
     * @param {number} year
     * @param {number} month
     * 要素はDateオブジェクト
     */
    function generateCalendarData(year, month) {
        const firstDayOfMonth = new Date(year, month - 1, 1).getDay(); // 月の初日
        const lastDateOfMonth = new Date(year, month, 0).getDate(); // 月の最終日
        const calendarData = [];
        let date = 1;

        // 最大6週間分のカレンダーを作成
        for (let i = 0; i < 6; i++) {
            const week = [];
            for (let j = 0; j < 7; j++) {
                if (i === 0 && j < firstDayOfMonth) {
                    // 月の初日より前は空
                    week.push(null);
                } else if (date > lastDateOfMonth) {
                    // 月の最終日を超えたら空
                    week.push(null);
                } else {
                    // 日付を追加
                    const newDate = new Date(year, month - 1, date);
                    week.push(newDate);
                    date++; //日付を増加させる
                }
            }
            calendarData.push(week);
        }
        return calendarData;
    }
    function selectDate(date) {
        if (date) {
            selectedDate.value = date;
            emit('dateSelected', date);
        }
    }

    // 前の月に移動する
    function prevMonth() {
        // 選択データの初期化
        selectedDate.value = new Date();
        let month = currentMonth.value - 1;
        let year = currentYear.value;

        if (month < 1) {
            month = 12;
            year -= 1;
        }

        currentMonth.value = month
        currentYear.value = year
        emit('yearMonth', formatYearMonth.value);
    }
    // 次の月に移動する
    function nextMonth() {
        // 選択データの初期化
        selectedDate.value = new Date();
        let month = currentMonth.value + 1;
        let year = currentYear.value;

        if (month > 12) {
            month = 1;
            year += 1;
        }

        currentMonth.value = month
        currentYear.value = year
        emit('yearMonth', formatYearMonth.value);
    }

    // レスポンスの形に変換
    function formatDate(date) {
        const monthNames = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
        const dayNames = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31'];
        const year = date.getFullYear();
        const month = monthNames[date.getMonth()];
        const day = dayNames[date.getDate() - 1];
        return `${year}/${month}/${day}`;
    }

    function isDateAvailavle(date) {
        if (!utils.isNull(props.reserveDateList)) {
            return props.reserveDateList.includes(date);
        } else {
            return false;
        }
    }

    function initializeSelect() {
        if (props.reserveDate === today) {
            selectedDate.value = today;
        } else {
            selectedDate.value = props.reserveDate;
        }
    }

    function reset() {
        if (props.resetFlag) {
            selectedDate.value = null;
        }
    }

    // ----------------------------
    // 算術プロパティ
    // ----------------------------
    const calendarData = computed(() => {
        return generateCalendarData(currentYear.value, currentMonth.value);
    });

    const formatYearMonth = computed(() => {
        const paddedMonth = String(currentMonth.value).padStart(2, '0');
        return `${currentYear.value}/${paddedMonth}`;
    });

    defineExpose({
        initializeSelect,
        reset,
    });
</script>

<template>
    <ul id="calendarTitle">
        <li>
            <button type="button" @click="prevMonth" :disabled="currentYear === minYear && currentMonth === minMonth">&nbsp;</button>
        </li>
        <li>
            <span v-if="languageStore.language === 'ja'">{{ currentYear + '年' }}</span>
            <span v-else>{{ currentYear }}</span>
        </li>
        <li>
            <span v-if="languageStore.language === 'ja'">{{ currentMonth + '月' }}</span>
            <span v-else>{{ months[parseInt(currentMonth, 10) - 1] }}</span>
        </li>
        <li>
            <button type="button" @click="nextMonth">&nbsp;</button>
        </li>
    </ul>
    <table id="calendarTable">
        <thead>
            <tr v-if="languageStore.language === 'ja'">
                <th v-for="day in weekDays" :key="day">
                    <span>{{ day }}</span>
                </th>
            </tr>
            <tr v-else>
                <th v-for="day in weekDaysEn" :key="day">
                    <span>{{ day }}</span>
                </th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="(week, index) in calendarData" :key="index">
                <template v-for="(date, cellIndex) in week" :key="cellIndex">
                    <template v-if="date === null">
                        <td class="none">
                            <span></span>
                        </td>
                    </template>
                    <template v-else-if="date && isDateAvailavle(formatDate(date))">
                        <template v-if="formatDate(date) === formatDate(today)">
                            <!--本日の日付-->
                            <td :class="{ today: true, selected: !utils.isNullEmpty(selectedDate) && formatDate(today) === formatDate(selectedDate) }">
                                <button type="button" @click="selectDate(date)">
                                    {{ date.getDate() }}
                                </button>
                            </td>
                        </template>
                        <template v-else>
                            <!--予約可能日-->
                            <td :class="{ selected: !utils.isNullEmpty(selectedDate) && formatDate(date) === formatDate(selectedDate) }">
                                <button type="button" @click="selectDate(date)">
                                    {{ date.getDate() }}
                                </button>
                            </td>
                        </template>
                    </template>
                    <template v-else>
                        <td class="none">
                            <span>
                                {{ date.getDate() }}
                            </span>
                        </td>
                    </template>
                </template>
            </tr>
        </tbody>
    </table>
</template>

<style></style>
